/**
 * Parsers for AI-generated response beat content.
 *
 * The LLM is instructed to wrap each character's dialogue in
 * `[CHARACTER:character_uuid:Character Name]` delimiters. It usually obeys,
 * but occasionally drifts to looser formats like `[Alice abc-123]` because
 * its system prompt also receives character info as `Name (ID: id)`. These
 * parsers tolerate that drift so the user always sees properly labeled
 * character blocks instead of raw bracket text.
 */

const STRICT_RE = /\[CHARACTER:([^:]+):([^\]]+)\]\s*([\s\S]*?)(?=\[CHARACTER:|$)/g

// Lenient header: line begins with `[Name id]` where the second token looks
// like an id (6+ alphanumeric/dash/underscore chars). Anchored to start of
// line so inline references like "I think [Bob abc-123] should help" inside a
// response body are NOT mistaken for new character headers.
const LENIENT_RE = /^[ \t]*\[([A-Za-z][\w'\- ]*?)\s+([A-Za-z0-9_-]{6,})\][ \t]*\r?\n([\s\S]*?)(?=^[ \t]*\[[A-Za-z][\w'\- ]*?\s+[A-Za-z0-9_-]{6,}\][ \t]*\r?\n|$)/gm

const STRICT_HEADER_RE = /\[CHARACTER:([^:]+):([^\]]+)\]\s*/g
const LENIENT_HEADER_RE = /^[ \t]*\[([A-Za-z][\w'\- ]*?)\s+([A-Za-z0-9_-]{6,})\][ \t]*$/gm

/**
 * Parse a character_response stream into an array of
 * `{ character_id, character_name, response }` blocks.
 *
 * Tries the strict `[CHARACTER:id:Name]` format first. If that yields no
 * matches, falls back to the lenient `[Name id]` format the LLM occasionally
 * produces. Returns an empty array if neither matches.
 */
export function parseCharacterStream(text) {
  if (typeof text !== 'string' || text.length === 0) return []

  const strict = []
  for (const m of text.matchAll(STRICT_RE)) {
    strict.push({
      character_id: m[1].trim(),
      character_name: m[2].trim(),
      response: m[3].trim(),
    })
  }
  if (strict.length > 0) return strict

  const lenient = []
  for (const m of text.matchAll(LENIENT_RE)) {
    // Lenient form: first token = name, second = id (opposite of strict).
    lenient.push({
      character_id: m[2].trim(),
      character_name: m[1].trim(),
      response: m[3].trim(),
    })
  }
  return lenient
}

/**
 * Parse a resolution stream — narrator prose interleaved with character
 * dialogue blocks — into segments. Each segment is either
 * `{ type: 'narrator', text }` or
 * `{ type: 'character', character_id, character_name, text }`.
 *
 * Tries the strict `[CHARACTER:id:Name]` headers first. Falls back to lenient
 * `[Name id]` headers if strict yields nothing. Returns an empty array on
 * non-string or empty input.
 */
export function parseResolutionStream(text) {
  if (typeof text !== 'string' || text.length === 0) return []

  const strictHeaders = collectHeaders(text, STRICT_HEADER_RE, ([id, name]) => ({
    character_id: id.trim(),
    character_name: name.trim(),
  }))
  if (strictHeaders.length > 0) return splitByHeaders(text, strictHeaders)

  const lenientHeaders = collectHeaders(text, LENIENT_HEADER_RE, ([name, id]) => ({
    character_id: id.trim(),
    character_name: name.trim(),
  }))
  return splitByHeaders(text, lenientHeaders)
}

/**
 * Find all delimiter headers in `text` using `re` (must be /g). Each match is
 * mapped through `extract` (which receives the capture groups) into a header
 * descriptor with `index`, `length`, and the extracted fields.
 */
function collectHeaders(text, re, extract) {
  const headers = []
  for (const m of text.matchAll(re)) {
    headers.push({
      index: m.index,
      length: m[0].length,
      ...extract(m.slice(1)),
    })
  }
  return headers
}

/**
 * Walk through the `text` and split it into narrator + character segments
 * using the discovered `headers`. A character's speech ends at the first
 * blank line after the header (or the next header, whichever comes first),
 * so trailing narrator prose between or after character blocks is captured
 * as its own narrator segment instead of being absorbed into a character.
 * Empty segments are dropped.
 */
function splitByHeaders(text, headers) {
  if (headers.length === 0) return []

  const segments = []
  let cursor = 0
  const blankLineRe = /\n[ \t]*\n/

  headers.forEach((h, i) => {
    const narratorText = text.slice(cursor, h.index).trim()
    if (narratorText) segments.push({ type: 'narrator', text: narratorText })

    const contentStart = h.index + h.length
    const nextHeaderStart = i + 1 < headers.length ? headers[i + 1].index : text.length
    const characterRegion = text.slice(contentStart, nextHeaderStart)

    const blankIndex = characterRegion.search(blankLineRe)
    const contentEnd = blankIndex >= 0
      ? contentStart + blankIndex
      : nextHeaderStart

    const characterText = text.slice(contentStart, contentEnd).trim()
    segments.push({
      type: 'character',
      character_id: h.character_id,
      character_name: h.character_name,
      text: characterText,
    })
    cursor = contentEnd
  })

  const trailing = text.slice(cursor).trim()
  if (trailing) segments.push({ type: 'narrator', text: trailing })

  return segments
}

/**
 * Resolve a character display name from the story's character list using the
 * id (or name) embedded in the parsed entry. Falls back to the entry's
 * character_name, then 'Unknown'.
 */
export function resolveCharacterName(entry, characters) {
  if (!characters?.length) return entry.character_name || 'Unknown'
  const match = characters.find(
    (c) => c.id === entry.character_id || c.id === entry.character_name
  )
  return match?.name || entry.character_name || 'Unknown'
}

/**
 * Detect the backend "characters pause" fallback so the frontend can avoid
 * overwriting good streamed text with this generic placeholder. The backend
 * returns it from `CharacterResponseGenerator#fallback_responses` whenever its
 * regex parse yields no characters.
 */
export function isFallbackCharacterResponse(data) {
  return (
    Array.isArray(data) &&
    data.length === 1 &&
    data[0]?.character_name === 'Narrator' &&
    typeof data[0]?.response === 'string' &&
    data[0].response.startsWith('The characters pause')
  )
}

/**
 * Strip the small set of inline SSML expression tags the model may emit
 * (<break/>, <emphasis>, <amazon:effect>, etc.) so the on-screen text reads as
 * clean prose. The tags only matter for synthesized speech — the player speaks
 * the marked-up version and displays the stripped version. Self-closing tags
 * like <break/> become a space so words don't run together; paired tags are
 * removed but their inner text is kept.
 */
export function stripSsml(text) {
  if (typeof text !== 'string' || text.length === 0) return text
  if (text.indexOf('<') === -1) return text
  return text
    .replace(/<break\b[^>]*\/?>/gi, ' ')        // pauses -> a space
    .replace(/<\/?(?:emphasis|amazon:effect|say-as|prosody|phoneme|speak)\b[^>]*>/gi, '')
    .replace(/\s{2,}/g, ' ')                      // tidy doubled spaces
    .trim()
}
